/**
 * 互认密钥所需的轻量密码原语：纯 JS SHA-256 + HMAC-SHA256 + 随机 nonce。
 *
 * 为什么不用 node:crypto / Web Crypto：shared 包被 Node（server/bridge）和浏览器
 * （mobile-web PWA）同时打包，直接用环境 API 会让 esbuild 在浏览器侧解析 node:crypto
 * 失败。这里用一份与标准实现完全一致的纯 JS 版本，两端算出的 HMAC 必然一致。
 * 输入限定为 ASCII（token 与 nonce 均为 hex），因此按字节处理即可。
 */

/** 循环右移（32 位无符号） */
function rotr(x: number, n: number): number {
  return (x >>> n) | (x << (32 - n));
}

const H0 = new Uint32Array([
  0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
]);

const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

/** 字节数组 → 16 进制小写字符串 */
function toHex(bytes: number[]): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function toStr(bytes: number[]): string {
  return String.fromCharCode(...bytes);
}

/** SHA-256 核心：输入 ASCII 字符串（每个 charCode 即一个字节），输出 32 字节原始摘要 */
function sha256Raw(input: string): number[] {
  const bytes: number[] = [];
  for (let i = 0; i < input.length; i++) bytes.push(input.charCodeAt(i) & 0xff);
  const bitLen = bytes.length * 8;
  bytes.push(0x80);
  while (bytes.length % 64 !== 56) bytes.push(0);
  bytes.push(0, 0, 0, 0, (bitLen >>> 24) & 0xff, (bitLen >>> 16) & 0xff, (bitLen >>> 8) & 0xff, bitLen & 0xff);

  const h = new Uint32Array(H0);
  for (let off = 0; off < bytes.length; off += 64) {
    const w = new Uint32Array(64);
    for (let i = 0; i < 16; i++) {
      w[i] =
        ((bytes[off + i * 4] << 24) |
          (bytes[off + i * 4 + 1] << 16) |
          (bytes[off + i * 4 + 2] << 8) |
          bytes[off + i * 4 + 3]) >>>
        0;
    }
    for (let i = 16; i < 64; i++) {
      const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
      const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0;
    }
    let a = h[0], b = h[1], c = h[2], d = h[3], e = h[4], f = h[5], g = h[6], hh = h[7];
    for (let i = 0; i < 64; i++) {
      const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const t1 = (hh + S1 + ch + K[i] + w[i]) >>> 0;
      const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (S0 + maj) >>> 0;
      hh = g; g = f; f = e; e = (d + t1) >>> 0; d = c; c = b; b = a; a = (t1 + t2) >>> 0;
    }
    h[0] = (h[0] + a) >>> 0; h[1] = (h[1] + b) >>> 0; h[2] = (h[2] + c) >>> 0; h[3] = (h[3] + d) >>> 0;
    h[4] = (h[4] + e) >>> 0; h[5] = (h[5] + f) >>> 0; h[6] = (h[6] + g) >>> 0; h[7] = (h[7] + hh) >>> 0;
  }
  const out: number[] = [];
  for (let i = 0; i < 8; i++) {
    out.push((h[i] >>> 24) & 0xff, (h[i] >>> 16) & 0xff, (h[i] >>> 8) & 0xff, h[i] & 0xff);
  }
  return out;
}

/** SHA-256：输入 ASCII 字符串，输出 64 位小写 hex */
export function sha256Hex(input: string): string {
  return toHex(sha256Raw(input));
}

const BLOCK = 64;

/** HMAC-SHA256：输入均为 ASCII 字符串，输出 64 位小写 hex */
export function hmacSha256Hex(key: string, msg: string): string {
  let k = key;
  if (k.length > BLOCK) k = toStr(sha256Raw(k)); // 超长 key 先哈希为 32 原始字节
  k = k.padEnd(BLOCK, '\x00').slice(0, BLOCK);
  let oPad = '';
  let iPad = '';
  for (let i = 0; i < BLOCK; i++) {
    const c = k.charCodeAt(i);
    oPad += String.fromCharCode(c ^ 0x5c);
    iPad += String.fromCharCode(c ^ 0x36);
  }
  // 内层摘要必须用 32 字节原始字节，不能用 hex 字符串
  const inner = toStr(sha256Raw(iPad + msg));
  return toHex(sha256Raw(oPad + inner));
}

/** 生成随机 nonce（32 位 hex） */
export function makeNonce(): string {
  const bytes = new Uint8Array(16);
  const c = globalThis.crypto;
  if (c && typeof c.getRandomValues === 'function') {
    c.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/** 服务器向客户端证明身份：HMAC(key=预共享 token, msg=客户端 nonce) */
export function makeProof(token: string, nonce: string): string {
  return hmacSha256Hex(token, nonce);
}
